import { useCallback, useEffect, useMemo, useRef } from "react"
import { useSelector } from "react-redux"
import { useChatComposer, useChatStore } from "@mui/x-chat-headless"
import type { ChatDraftAttachment, ChatDraftAttachmentStatus } from "@mui/x-chat-headless"
import { getNeedSignTerms } from "modules/account-root/selectors"
import { useUserId } from "resources/account"
import { fileExtension } from "helpers/formatters/file-extension"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { getAttachMethod } from "./attach-method"
import type { Attachment } from "./attachments-transport"
import { AttachmentUploadError, UPLOAD_FAILED_MESSAGE, uploadAttachment } from "./attachments-transport"
import { useAnalytics } from "connected-components/analytics"
import { useLatest } from "./use-latest"

interface TrackedUpload {
  controller: AbortController
  settled: Promise<string>
}

export interface AttachmentUploads {
  resolveAttachmentIds: (drafts: readonly ChatDraftAttachment[]) => Promise<string[]>
  reuploadAttachment: (draft: ChatDraftAttachment) => Promise<string>
  hasBlockingError: boolean
}

export interface UseAttachmentUploadsParams {
  accountAlias: string
  threadId: string | null
}

export function useAttachmentUploads({ accountAlias, threadId }: UseAttachmentUploadsParams): AttachmentUploads {
  const { attachments } = useChatComposer()
  const store = useChatStore()
  const userId = useUserId()
  const needSignTerms = useSelector(getNeedSignTerms)
  const analytics = useAnalytics()

  const needSignTermsRef = useLatest(needSignTerms)
  const threadIdRef = useLatest(threadId)
  const userIdRef = useLatest(userId)
  const analyticsRef = useLatest(analytics)

  const attachmentIdByLocalId = useRef<Map<string, string>>(new Map())
  const failureByLocalId = useRef<Map<string, string>>(new Map())
  const inFlightByLocalId = useRef<Map<string, TrackedUpload>>(new Map())
  const resolvingLocalIds = useRef<Set<string>>(new Set())

  const patchDraft = useCallback((localId: string, status: ChatDraftAttachmentStatus) => {
    const current = store.state.composerAttachments
    const index = current.findIndex((entry) => entry.localId === localId)
    if (index < 0) return
    if (current[index].status === status) return
    const next = [...current]
    next[index] = { ...current[index], status }
    store.setComposerAttachments(next)
  }, [store])

  const startUpload = useCallback((draft: ChatDraftAttachment): TrackedUpload => {
    const controller = new AbortController()
    const settled = uploadAttachment({
      accountAlias,
      threadId: threadIdRef.current,
      file: draft.file,
      signal: controller.signal,
      auth: {
        account: accountAlias,
        userId: userIdRef.current,
        getNeedSignTerms: () => needSignTermsRef.current,
      },
    }).then((attachment: Attachment) => {
      attachmentIdByLocalId.current.set(draft.localId, attachment.id)
      failureByLocalId.current.delete(draft.localId)
      inFlightByLocalId.current.delete(draft.localId)
      patchDraft(draft.localId, "uploaded")
      const extension = fileExtension(attachment.filename)
      analyticsRef.current.track(AI_TEAMMATE_EVENTS.CHAT_ATTACHMENT_UPLOADED, {
        mediaType: attachment.mediaType,
        ...(extension !== undefined && { extension }),
        sizeBytes: attachment.sizeBytes,
        attachMethod: getAttachMethod(draft.file),
      })
      return attachment.id
    }).catch((error: unknown) => {
      inFlightByLocalId.current.delete(draft.localId)
      if (controller.signal.aborted) throw error
      const message = error instanceof Error && error.message !== "" ? error.message : UPLOAD_FAILED_MESSAGE
      failureByLocalId.current.set(draft.localId, message)
      patchDraft(draft.localId, "error")
      throw error
    })
    const tracked: TrackedUpload = { controller, settled }
    inFlightByLocalId.current.set(draft.localId, tracked)
    return tracked
  }, [accountAlias, patchDraft, threadIdRef, userIdRef, needSignTermsRef, analyticsRef])

  useEffect(() => {
    const live = new Set(attachments.map((entry) => entry.localId))
    for (const [localId, tracked] of inFlightByLocalId.current) {
      if (!live.has(localId) && !resolvingLocalIds.current.has(localId)) {
        tracked.controller.abort()
        inFlightByLocalId.current.delete(localId)
      }
    }
    for (const draft of attachments) {
      if (draft.status !== "queued") continue
      if (inFlightByLocalId.current.has(draft.localId)) continue
      if (attachmentIdByLocalId.current.has(draft.localId)) continue
      const tracked = startUpload(draft)
      tracked.settled.catch(() => undefined)
      patchDraft(draft.localId, "uploading")
    }
  }, [attachments, startUpload, patchDraft])

  // A thread switch remounts ChatBox and drops the drafts. An upload left running would write to a store the
  // user can no longer send from, and would report an upload for a file that never reaches a message.
  useEffect(() => {
    const inFlight = inFlightByLocalId.current
    return () => {
      for (const tracked of inFlight.values()) tracked.controller.abort()
      inFlight.clear()
    }
  }, [])

  const resolveAttachmentIds = useCallback(async (drafts: readonly ChatDraftAttachment[]): Promise<string[]> => {
    for (const draft of drafts) resolvingLocalIds.current.add(draft.localId)
    try {
      const ids: string[] = []
      for (const draft of drafts) {
        const failure = failureByLocalId.current.get(draft.localId)
        if (draft.status === "error" || failure !== undefined) {
          throw new AttachmentUploadError(failure ?? UPLOAD_FAILED_MESSAGE, null)
        }
        const known = attachmentIdByLocalId.current.get(draft.localId)
        if (known !== undefined) {
          ids.push(known)
          continue
        }
        const tracked = inFlightByLocalId.current.get(draft.localId) ?? startUpload(draft)
        ids.push(await tracked.settled)
      }
      return ids
    } finally {
      for (const draft of drafts) resolvingLocalIds.current.delete(draft.localId)
    }
  }, [startUpload])

  const reuploadAttachment = useCallback(async (draft: ChatDraftAttachment): Promise<string> => {
    attachmentIdByLocalId.current.delete(draft.localId)
    failureByLocalId.current.delete(draft.localId)
    resolvingLocalIds.current.add(draft.localId)
    try {
      return await startUpload(draft).settled
    } finally {
      resolvingLocalIds.current.delete(draft.localId)
    }
  }, [startUpload])

  const hasBlockingError = attachments.some((draft) => draft.status === "error")

  return useMemo(
    () => ({ resolveAttachmentIds, reuploadAttachment, hasBlockingError }),
    [resolveAttachmentIds, reuploadAttachment, hasBlockingError],
  )
}
